import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../lib/api-client.js';

export interface ActivityEvent {
  id: string | number;
  email?: string;
  manager_name?: string;
  category?: string;
  action?: string;
  detail?: string | Record<string, unknown>;
  created_at?: string;
}

export interface ActivitySummary {
  total: number;
  uniqueActors: number;
  categoryCount: number;
  latestAt: string | null;
}

export interface ClockEvent {
  id: string | number;
  email?: string;
  manager_name?: string;
  category?: string;
  action?: string;
  detail?: string | Record<string, unknown>;
  created_at?: string;
}

export interface ClockSummary {
  clocked_in: number;
  clocked_out: number;
}

export interface ClockedInEmployee {
  id: string | number;
  name?: string;
  driver_id?: string | null;
  clock_in_timestamp?: string;
}

interface ActivityResponse {
  activity?: ActivityEvent[];
  error?: string;
}

interface ClockOverviewResponse {
  summary?: ClockSummary;
  current_clocked_in?: ClockedInEmployee[];
  clock_events?: ClockEvent[];
  error?: string;
}

export function useActivityReadModel(filterDate: string, filterCategory: string, filterEmail: string) {
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [clockSummary, setClockSummary] = useState<ClockSummary | null>(null);
  const [clockEvents, setClockEvents] = useState<ClockEvent[]>([]);
  const [currentClockedIn, setCurrentClockedIn] = useState<ClockedInEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (filterDate) params.set('date', filterDate);
      if (filterCategory && filterCategory !== 'all') params.set('category', filterCategory);
      if (filterEmail) params.set('email', filterEmail);
      params.set('limit', '100');

      const [data, clockData] = await Promise.all([
        apiFetch<ActivityResponse>(`/api/manager-activity?${params.toString()}`),
        apiFetch<ClockOverviewResponse>(`/api/activity/clock-overview?date=${encodeURIComponent(filterDate)}`),
      ]);
      if (data?.error) throw new Error(data.error);
      if (clockData?.error) throw new Error(clockData.error);

      setActivity(data.activity || []);
      setClockSummary(clockData.summary || null);
      setClockEvents(clockData.clock_events || []);
      setCurrentClockedIn(clockData.current_clocked_in || []);
    } catch (err) {
      setActivity([]);
      setClockSummary(null);
      setClockEvents([]);
      setCurrentClockedIn([]);
      setError(err instanceof Error ? err.message : 'Activity load failed');
    } finally {
      setLoading(false);
    }
  }, [filterDate, filterCategory, filterEmail]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo<ActivitySummary | null>(() => {
    if (!activity.length) return null;

    const actors = new Set<string>();
    const categories = new Set<string>();
    let latest: string | null = null;

    activity.forEach((event) => {
      if (event.email) actors.add(event.email);
      if (event.category) categories.add(event.category);
      if (!latest || new Date(event.created_at || 0) > new Date(latest)) {
        latest = event.created_at || null;
      }
    });

    return {
      total: activity.length,
      uniqueActors: actors.size,
      categoryCount: categories.size,
      latestAt: latest,
    };
  }, [activity]);

  return {
    activity,
    summary,
    clockSummary,
    clockEvents,
    currentClockedIn,
    loading,
    error,
    refreshActivity: load,
  };
}
